// Quote Builder — PDF generation.
//
// Uses jsPDF + jspdf-autotable to produce A4 portrait PDFs that match
// source-of-truth `_qbv3ExportCustomerPDF` / `_qbv3ExportInternalPDF`
// (kos-qb-v3.js:5239-5530) — multi-page, embedded design images,
// per-supplier breakdown on internal copy.
//
// Two outputs:
//   • Customer PDF — retailer-branded, line items, totals (ex-VAT / VAT /
//     grand inc-VAT). Embeds ONLY designs marked `visibility === 'shared'`.
//     Trade prices, cost, profit, margin are NEVER on this copy.
//   • Internal PDF — full pricing (List / Disc% / Trade / Markup / Retail /
//     Line) plus a per-supplier breakdown page. Embeds ALL attached designs
//     regardless of share status.
//
// Retailer details (business name, address, Twilio number, logo, email,
// website) are read fresh from PG via the retailer-profile endpoint —
// never hardcoded. The platform Twilio number is the only contact number
// shown on customer-facing PDFs; the retailer's personal mobile is never
// exposed by the server.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import type { QuoteBuilderState, QuoteItem } from "./types";
import type { PageContext } from "../../shell/types";
import { quoteTotals } from "./pricing";
import { customerLineDescription, customerSpecSummary } from "./customer-copy";
import { fetchRetailerProfile, formatAddressBlock, type RetailerProfile } from "./retailer-profile";

export type Mode = "customer" | "internal";
type Visibility = "shared" | "hidden";

// ── Source-of-truth orange (kos-qb-v3.js ORANGE constant). Matches the
// SOT-pinned QB exception in [[feedback-rgba-tint-palette]] —
// retailer-branded PDF chrome uses the orange-on-white palette source uses.
const ORANGE_RGB: [number, number, number] = [229, 130, 0];
const GREEN_RGB: [number, number, number]  = [34, 197, 94];
const GREY_RGB: [number, number, number]   = [120, 120, 120];

interface DesignRow {
  id: string;
  name: string;
  ext: string;
  dataUrl: string;
  visibility: Visibility;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function fmtGBP(pounds: number): string {
  const n = Number.isFinite(pounds) ? pounds : 0;
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);
}

// jsPDF's built-in helvetica uses WinAnsiEncoding — glyphs outside that
// range (emoji, arrows, U+2212 minus, em-dash variants) render as boxes
// and visually break adjacent text. Confine PDF strings to ASCII + the
// latin-1 supplement set helvetica actually supports.
/**
 * The per-line manufacturing spec, as the internal PDF renders it.
 *
 * EXPORTED so the purchase order draws on the SAME function (see
 * place-order.ts). The PO used to carry only code / description / qty / price
 * / zone, so hinge side, edging, reduced gable, blanking panel and the cut
 * dimensions reached the internal PDF and the screen but never the supplier.
 * Reimplementing this list on the PO side would have fixed that once and then
 * drifted; sharing it means the two documents cannot disagree about what was
 * ordered.
 */
export function pdfSpecLines(it: QuoteItem): string[] {
  const parts: string[] = [];
  if (it.category === "doors") {
    if (it.doorRange) parts.push(String(it.doorRange));
    if (it.doorColour) parts.push(String(it.doorColour));
    if (it.width && it.height) parts.push(`${it.width}x${it.height}mm`);
    if (it.handing) parts.push(`handing ${it.handing}`);
    if (it.integratedHandle && it.integratedHandle !== "none") parts.push(`integrated handle ${it.integratedHandle}`);
  } else if (it.category === "cabinets") {
    if (it.unitTypeLabel) parts.push(String(it.unitTypeLabel));
    if (it.height && it.width && it.depth) parts.push(`${it.height}x${it.width}x${it.depth}mm`);
    if (it.carcassFinish) parts.push(`carcass ${it.carcassFinish}`);
    if (it.edging) parts.push(`front edged ${it.edging}`);
    if (it.hinging) parts.push(`hinged ${it.hinging}`);
  } else if (it.category === "worktops") {
    if (it.material) parts.push(String(it.material));
    if (it.length || it.width || it.thickness) parts.push(`${it.length || 0}x${it.width || 0}x${it.thickness || 0}mm`);
  } else {
    if (it.width || it.height || it.depth) parts.push(`${it.width || 0}x${it.height || 0}x${it.depth || 0}mm`);
  }
  return parts;
}

// Customer-facing line copy (description strip + one-line spec summary) lives
// in customer-copy.ts, shared with the send-to-portal payload in actions.ts.
// It replaced a dedupeNoteAgainstSpecs() helper that tried to subtract the
// spec bullets from the note and print the remainder: it removed fragments
// that RESTATED a bullet but kept every fragment that did not, which is why
// the board discount, hinge side, blanking panel and cut-to-size deviations
// were still reaching customers. A whitelist was the only way to stop that.

async function fetchAttachedDesigns(
  ctx: PageContext,
  attachedIds: string[],
): Promise<DesignRow[]> {
  if (attachedIds.length === 0) return [];
  try {
    interface ServerDesign {
      id: string;
      name: string;
      ext: string;
      dataUrl: string;
      visibility: string;
    }
    const res = await ctx.api<{ ok: boolean; designs: ServerDesign[] }>("/portal/portal-designs");
    const byId = new Map((res.designs ?? []).map((d) => [d.id, d]));
    const out: DesignRow[] = [];
    for (const id of attachedIds) {
      const d = byId.get(id);
      if (!d) continue;
      const ext = (d.ext || "").toLowerCase();
      // Only image formats can be embedded in the PDF via doc.addImage.
      if (ext !== "jpg" && ext !== "jpeg" && ext !== "png") continue;
      if (!d.dataUrl) continue;
      out.push({
        id: d.id,
        name: d.name,
        ext,
        dataUrl: d.dataUrl,
        visibility: d.visibility === "shared" ? "shared" : "hidden",
      });
    }
    return out;
  } catch (err) {
    console.warn("[qb-pdf] fetch designs failed", err);
    return [];
  }
}

// ── Page chrome (header + footer drawn on every page) ────────────────────

function drawHeaderBar(
  doc: jsPDF,
  profile: RetailerProfile | null,
  state: QuoteBuilderState,
  mode: Mode,
): void {
  const pageW = doc.internal.pageSize.getWidth();
  // Solid orange bar
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageW, 22, "F");

  // Optional logo (left edge inside bar)
  let textX = 14;
  if (profile?.logoUrl) {
    try {
      doc.addImage(profile.logoUrl, "PNG", 12, 4, 14, 14);
      textX = 30;
    } catch {
      // bad logoUrl — fall back to text-only header
    }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(profile?.businessName || "Kitchengine", textX, 11);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  const subLeft = mode === "internal"
    ? "INTERNAL — RETAILER COPY (cost + profit visible)"
    : `Quote for ${state.customer || "customer"}`;
  doc.text(subLeft, textX, 17);

  if (state.reference) {
    doc.text(state.reference, pageW - 14, 17, { align: "right" });
  }
}

function drawFooter(doc: jsPDF, profile: RetailerProfile | null, mode: Mode): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setDrawColor(220, 220, 220);
  doc.line(14, pageH - 18, pageW - 14, pageH - 18);
  doc.setFontSize(7.5);
  doc.setTextColor(...GREY_RGB);
  doc.setFont("helvetica", "normal");

  const left: string[] = [];
  if (profile) {
    if (profile.businessName) left.push(profile.businessName);
    const addr = formatAddressBlock(profile).join(", ");
    if (addr) left.push(addr);
    // VAT number is legally required on VAT documents — show it when set.
    if (profile.vatNumber) left.push(`VAT No: ${profile.vatNumber}`);
  }
  doc.text(left.join("  ·  "), 14, pageH - 12);

  const right: string[] = [];
  if (profile?.twilioNumber) right.push(profile.twilioNumber);
  // The retailer's email is never shown on the customer-facing copy — the
  // platform Twilio number is the customer's contact channel (mirrors the
  // contact-masking rules). It stays on the internal/retailer copy.
  if (mode === "internal" && profile?.email) right.push(profile.email);
  if (profile?.website) right.push(profile.website);
  if (right.length) {
    doc.text(right.join("  ·  "), pageW - 14, pageH - 12, { align: "right" });
  }

  doc.setFontSize(6.5);
  const pageInfo = `Page ${doc.getCurrentPageInfo().pageNumber}`;
  doc.text(pageInfo, pageW - 14, pageH - 7, { align: "right" });
}

function paintAllPages(doc: jsPDF, profile: RetailerProfile | null, state: QuoteBuilderState, mode: Mode): void {
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    drawHeaderBar(doc, profile, state, mode);
    drawFooter(doc, profile, mode);
  }
}

// ── Body sections ────────────────────────────────────────────────────────

function drawMetaBlock(
  doc: jsPDF,
  state: QuoteBuilderState,
  profile: RetailerProfile | null,
  customerAddress: string,
): number {
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(state.project || state.customer || "Kitchen Quote", 14, 34);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  const meta = [
    `Date: ${state.date || "—"}`,
    `Valid for ${state.validDays || 30} days`,
    `Status: ${state.status}`,
  ].join("   |   ");
  doc.text(meta, 14, 41);

  // From / To address columns so both parties' addresses appear on the quote.
  const pageW = doc.internal.pageSize.getWidth();
  const rx = pageW - 14;

  // "Prepared for" (customer) — left column.
  let lY = 49;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(90, 90, 90);
  doc.text("Prepared for", 14, lY);
  lY += 4.5;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  doc.text(state.customer || "—", 14, lY);
  lY += 4;
  if (customerAddress) {
    const lines = doc.splitTextToSize(customerAddress, 80);
    doc.text(lines, 14, lY);
    lY += (Array.isArray(lines) ? lines.length * 4 : 4);
  }

  // "From" (retailer) — right column, right-aligned.
  let rY = 49;
  if (profile) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(90, 90, 90);
    doc.text("From", rx, rY, { align: "right" });
    rY += 4.5;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    if (profile.businessName) { doc.text(profile.businessName, rx, rY, { align: "right" }); rY += 4; }
    for (const line of formatAddressBlock(profile)) { doc.text(line, rx, rY, { align: "right" }); rY += 4; }
  }

  return Math.max(lY, rY) + 4;
}

/** EXPORTED for `__tests__/verify-qb-customer-pdf.ts`, which counts the pages
 *  an 84-line quote produces. Page count is a property of the row heights this
 *  function builds, so the test has to drive the real thing — reimplementing
 *  the table in the test would only ever prove the test right. */
export function buildLinesTable(state: QuoteBuilderState, mode: Mode): {
  head: string[][];
  body: Array<Array<string | { content: string; colSpan?: number; styles?: Record<string, unknown> }>>;
  columnStyles: Record<number, { halign?: "left" | "center" | "right"; cellWidth?: number | "auto" }>;
} {
  const isInternal = mode === "internal";
  const head = isInternal
    ? [["Code", "Description / spec", "Qty", "List £", "Disc%", "Trade £", "Mkup", "Retail £", "Line £"]]
    : [["Description / spec", "Qty", "Unit price", "Line total"]];

  const customerColStyles = {
    0: { cellWidth: "auto" as const },
    1: { halign: "right" as const, cellWidth: 14 },
    2: { halign: "right" as const, cellWidth: 24 },
    3: { halign: "right" as const, cellWidth: 26 },
  };
  // 9-col internal table on A4 portrait (usable width ~182mm at 14mm
  // margins). Fixed cols sum to 138mm leaving ~44mm for the description
  // column — tight, so we drop Code to 18mm and use 10mm/13mm increments
  // on the numeric cells. wider description prevents word-break artefacts
  // that read as "text cut off" in screenshots.
  const internalColStyles = {
    0: { cellWidth: 18 },
    1: { cellWidth: "auto" as const },
    2: { halign: "right" as const, cellWidth: 10 },
    3: { halign: "right" as const, cellWidth: 14 },
    4: { halign: "right" as const, cellWidth: 12 },
    5: { halign: "right" as const, cellWidth: 14 },
    6: { halign: "right" as const, cellWidth: 14 },
    7: { halign: "right" as const, cellWidth: 14 },
    8: { halign: "right" as const, cellWidth: 16 },
  };

  type Row = Array<string | { content: string; colSpan?: number; styles?: Record<string, unknown> }>;
  const body: Row[] = [];
  for (const z of state.zones) {
    body.push([
      {
        content: z.name.toUpperCase() + (z.note ? ` - ${z.note}` : ""),
        colSpan: head[0].length,
        styles: { fillColor: [40, 40, 46], textColor: [255, 255, 255], fontStyle: "bold" },
      },
    ]);
    const summary = !isInternal && state.zoneSummary[z.id] === true;
    if (summary) {
      const retailGbp = z.items.reduce((s, it) => s + (it.qty || 0) * (it.retailPrice || 0), 0);
      // Retailer-written plain-English description replaces the SKU breakdown.
      // Falls back to a generic line when the summary text is left blank.
      const summaryText = (z.summary || "").trim()
        || `${z.name} — ${z.items.length} item${z.items.length === 1 ? "" : "s"} (full breakdown on request)`;
      body.push([
        summaryText,
        "",
        "",
        fmtGBP(retailGbp),
      ]);
      continue;
    }
    for (const it of z.items) {
      const qty = it.qty || 0;
      const tp = it.tradePrice || 0;
      const rp = it.retailPrice || 0;
      const lineTotal = qty * rp;
      const specs = pdfSpecLines(it);
      if (isInternal) {
        const descCell = (it.desc || "") + (specs.length ? `\n  > ${specs.join(" · ")}` : "") + (it.notes ? `\n  Notes: ${it.notes}` : "");
        const mkup = it.markupMode === "gbp"
          ? `£${(it.markupGbp || 0).toFixed(2)}`
          : `${(it.markupPct || 0).toFixed(1)}%`;
        body.push([
          it.code || "",
          descCell,
          String(qty),
          (it.listPrice || 0).toFixed(2),
          `${(it.discPct || 0).toFixed(1)}%`,
          tp.toFixed(2),
          mkup,
          rp.toFixed(2),
          lineTotal.toFixed(2),
        ]);
      } else {
        // Two lines at most: the product, then a short spec summary. The old
        // layout put every spec on its own bullet line and then appended the
        // note, which on the 84-line reference quote averaged 22.4mm a row and
        // ran to 9 pages; this averages 12.7mm and 5.
        const summary = customerSpecSummary(it);
        const descCell = customerLineDescription(it) + (summary ? `\n${summary}` : "");
        body.push([
          descCell,
          String(qty),
          fmtGBP(rp),
          fmtGBP(lineTotal),
        ]);
      }
    }
  }
  return { head, body, columnStyles: isInternal ? internalColStyles : customerColStyles };
}

/**
 * Draw the line-item table.
 *
 * EXPORTED, and extracted out of exportPdf, so `verify-qb-customer-pdf.ts` can
 * count the pages an 84-line quote produces against the REAL layout. Page
 * count is a product of the row heights and these exact style options; a test
 * that restated them would pass while the shipped options drifted.
 */
export function drawLinesTable(
  doc: jsPDF,
  state: QuoteBuilderState,
  mode: Mode,
  startY: number,
): void {
  const { head, body, columnStyles } = buildLinesTable(state, mode);
  autoTable(doc, {
    startY,
    head,
    body,
    styles: { fontSize: 8.5, cellPadding: 2.5, valign: "top", overflow: "linebreak" },
    headStyles: { fillColor: ORANGE_RGB, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles,
    alternateRowStyles: { fillColor: [248, 248, 248] },
    margin: { left: 14, right: 14, top: 28, bottom: 22 },
  });
}

/**
 * Page-break guard for the blocks drawn AFTER the line-item table.
 *
 * autoTable paginates itself; everything that follows it was writing at
 * whatever `y` the table happened to stop at, with no bottom check at all.
 * Two ways that showed up on a long quote: a totals block drawn below the
 * footer rule (money the customer never sees), and — once drawPaymentTerms'
 * own late guard fired — a final page carrying nothing but "Payment terms"
 * and three short lines, which is half of the "many pages, a few characters
 * each" report.
 *
 * `needed` is the height of the whole block, so a block is never split: it
 * moves to the next page intact or not at all. Returns the y to draw at.
 */
function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  // 22mm bottom margin — the same figure autoTable is given, and the footer
  // rule sits at pageH - 18.
  const limit = doc.internal.pageSize.getHeight() - 22;
  if (y + needed <= limit) return y;
  doc.addPage();
  return 34;
}

function drawTotals(doc: jsPDF, state: QuoteBuilderState, mode: Mode, startY: number): number {
  const pageW = doc.internal.pageSize.getWidth();
  const t = quoteTotals(state);
  const col1 = pageW - 80;
  const col2 = pageW - 14;
  // 4 cost rows (internal only) + subtotal + optional delivery + optional VAT
  // + rule + grand total. Measured, not guessed, so the block never splits.
  const rows = (mode === "internal" ? 4 : 0) + 1
    + (t.deliveryPence > 0 ? 1 : 0)
    + (t.vatAmountPence + t.deliveryVatPence > 0 ? 1 : 0);
  let y = ensureSpace(doc, startY, rows * 5 + 14);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);

  if (mode === "internal") {
    doc.text("List cost", col1, y, { align: "right" });
    doc.text(fmtGBP(t.listCostPence / 100), col2, y, { align: "right" });
    y += 5;
    doc.text("Discount", col1, y, { align: "right" });
    doc.text(`-${fmtGBP(t.discountPence / 100)}`, col2, y, { align: "right" });
    y += 5;
    doc.text("Trade cost", col1, y, { align: "right" });
    doc.text(fmtGBP(t.tradeCostPence / 100), col2, y, { align: "right" });
    y += 5;
    doc.setTextColor(...GREEN_RGB);
    doc.text("Profit", col1, y, { align: "right" });
    doc.text(`${fmtGBP(t.profitPence / 100)} (${t.marginPct.toFixed(1)}%)`, col2, y, { align: "right" });
    y += 5;
    doc.setTextColor(80, 80, 80);
  }

  // VAT only appears when the quote actually carries it (a non-VAT-registered
  // retailer produces ex-VAT quotes — no VAT line, "Total" not "inc VAT").
  const vatPence = t.vatAmountPence + t.deliveryVatPence;
  const vatApplies = vatPence > 0;
  doc.text(vatApplies ? "Subtotal (ex-VAT)" : "Subtotal", col1, y, { align: "right" });
  doc.text(fmtGBP(t.retailSubPence / 100), col2, y, { align: "right" });
  y += 5;
  if (t.deliveryPence > 0) {
    doc.text(vatApplies ? "Delivery (ex VAT)" : "Delivery", col1, y, { align: "right" });
    doc.text(fmtGBP(t.deliveryPence / 100), col2, y, { align: "right" });
    y += 5;
  }
  if (vatApplies) {
    doc.text(`VAT (${state.vatRate}%)`, col1, y, { align: "right" });
    doc.text(fmtGBP(vatPence / 100), col2, y, { align: "right" });
    y += 4;
  }
  doc.setDrawColor(...ORANGE_RGB);
  doc.line(col1 - 22, y, col2, y);
  y += 7;
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...ORANGE_RGB);
  doc.text(vatApplies ? "Grand total inc VAT" : "Total", col1, y, { align: "right" });
  doc.text(fmtGBP(t.grandTotalPence / 100), col2, y, { align: "right" });
  return y + 6;
}

/** Cover note above the line items (customer-facing copy). */
function drawIntroNote(doc: jsPDF, state: QuoteBuilderState, startY: number): number {
  const text = (state.introNote ?? "").trim();
  if (!text) return startY;
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFontSize(9.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  const lines = doc.splitTextToSize(text, pageW - 28);
  const y = startY + 2;
  doc.text(lines, 14, y);
  return y + (Array.isArray(lines) ? lines.length : 1) * 4.4 + 4;
}

/** Closing note below the totals (customer-facing copy). */
function drawClosingNote(doc: jsPDF, state: QuoteBuilderState, startY: number): number {
  const text = (state.closingNote ?? "").trim();
  if (!text) return startY;
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFontSize(9.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  const lines = doc.splitTextToSize(text, pageW - 28);
  const n = Array.isArray(lines) ? lines.length : 1;
  const y = ensureSpace(doc, startY + 8, n * 4.4 + 2);
  doc.text(lines, 14, y);
  return y + n * 4.4 + 2;
}

function drawNotesAndTerms(doc: jsPDF, state: QuoteBuilderState, startY: number): number {
  const pageW = doc.internal.pageSize.getWidth();
  let y = startY + 8;
  if (state.notes) {
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    const lines = doc.splitTextToSize(state.notes, pageW - 28);
    const n = Array.isArray(lines) ? lines.length : 1;
    y = ensureSpace(doc, y, n * 4 + 4);
    doc.text(lines, 14, y);
    y += (n * 4) + 4;
  }
  if (state.termsAndConditions) {
    const lines = doc.splitTextToSize(state.termsAndConditions, pageW - 28);
    const n = Array.isArray(lines) ? lines.length : 1;
    // Heading + body kept together: a "Terms & conditions" heading alone at
    // the foot of a page is the near-empty page in miniature.
    y = ensureSpace(doc, y, 5 + (n * 3.5) + 2);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(60, 60, 60);
    doc.text("Terms & conditions", 14, y);
    y += 5;
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(lines, 14, y);
    y += (n * 3.5) + 2;
  }
  return y;
}

// Payment schedule (retailer's saved default terms) — appended to the foot of
// the quote so the customer sees how/when payment is due. Read from
// GET /api/payment-schedule/default by the caller. Shown on both copies.
function drawPaymentTerms(
  doc: jsPDF,
  stages: Array<{ label?: string; pct?: number; trigger?: string }>,
  startY: number,
): void {
  if (!stages.length) return;
  // Was `if (y > pageH - 40) addPage()` — a fixed 40mm that ignored how many
  // stages there are, so a 5-stage schedule still ran off the bottom while a
  // 2-stage one took a whole page it did not need.
  let y = ensureSpace(doc, startY + 8, 5 + stages.length * 4.5);
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(60, 60, 60);
  doc.text("Payment terms", 14, y);
  y += 5;
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  const prettyTrigger = (t?: string): string => {
    const k = String(t || "").toLowerCase().replace(/[_-]/g, " ").trim();
    return k ? k.charAt(0).toUpperCase() + k.slice(1) : "";
  };
  for (const s of stages) {
    const pct = typeof s.pct === "number" ? s.pct : 0;
    const trg = prettyTrigger(s.trigger);
    doc.text(`${s.label || "Payment"} — ${pct}%${trg ? ` (${trg})` : ""}`, 14, y);
    y += 4.5;
  }
}

function drawSupplierBreakdown(doc: jsPDF, state: QuoteBuilderState): void {
  const t = quoteTotals(state);
  const keys = Object.keys(t.bySupplier);
  if (keys.length === 0) return;
  doc.addPage();
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(30, 30, 30);
  doc.text("Per-supplier breakdown", 14, 34);

  const rows = keys.map((s) => {
    const v = t.bySupplier[s];
    const tradeGbp = v.tradePence / 100;
    const retailGbp = v.retailPence / 100;
    const profitGbp = retailGbp - tradeGbp;
    return [s, String(v.items), fmtGBP(tradeGbp), fmtGBP(retailGbp), fmtGBP(profitGbp)];
  });

  autoTable(doc, {
    startY: 40,
    head: [["Supplier", "Lines", "Trade", "Retail", "Profit"]],
    body: rows,
    styles: { fontSize: 10, cellPadding: 3 },
    headStyles: { fillColor: [60, 60, 64], textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: {
      1: { halign: "right" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
    },
    margin: { left: 14, right: 14, top: 28, bottom: 22 },
  });
}

function drawDesignsPages(doc: jsPDF, designs: DesignRow[]): void {
  if (designs.length === 0) return;
  const pageW = doc.internal.pageSize.getWidth();
  doc.addPage();
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(30, 30, 30);
  doc.text("Attached designs", 14, 34);

  let y = 42;
  const imgW = pageW - 28;
  const imgH = 80;
  for (const d of designs) {
    if (y + imgH + 8 > doc.internal.pageSize.getHeight() - 24) {
      doc.addPage();
      y = 34;
    }
    try {
      doc.addImage(d.dataUrl, d.ext === "png" ? "PNG" : "JPEG", 14, y, imgW, imgH);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(d.name, 14, y + imgH + 4);
      y += imgH + 14;
    } catch (err) {
      console.warn("[qb-pdf] addImage failed", err);
    }
  }
}

// ── Public API ───────────────────────────────────────────────────────────

async function exportPdf(
  state: QuoteBuilderState,
  mode: Mode,
  ctx: PageContext,
): Promise<void> {
  const profile = await fetchRetailerProfile();
  // Retailer's saved payment terms (50/40/10 etc.) for the foot of the quote.
  let paymentStages: Array<{ label?: string; pct?: number; trigger?: string }> = [];
  try {
    const res = await ctx.api<{ ok?: boolean; schedule?: Array<{ label?: string; pct?: number; trigger?: string }> }>(
      "/payment-schedule/default",
    );
    if (res && Array.isArray(res.schedule)) paymentStages = res.schedule;
  } catch { /* terms are best-effort — omit the block if unavailable */ }
  // Customer address for the "Prepared for" block — resolved from the CRM
  // record by id (the QB state doesn't carry a full address). Best-effort.
  let customerAddress = "";
  if (state.customerId) {
    try {
      const res = await ctx.api<{ contacts?: Array<{ id?: string; address?: string }> }>("/crm/contacts");
      const c = (res.contacts || []).find((x) => x.id === state.customerId);
      if (c && typeof c.address === "string") customerAddress = c.address;
    } catch { /* best-effort — omit the customer address line */ }
  }
  const allDesigns = await fetchAttachedDesigns(ctx, state.attachedDesigns);
  // Customer PDF: ONLY share=shared designs.  Internal PDF: every attached file.
  const designsToEmbed = mode === "customer"
    ? allDesigns.filter((d) => d.visibility === "shared")
    : allDesigns;

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const metaEndY = drawMetaBlock(doc, state, profile, customerAddress);
  // Cover note above the line items.
  const afterIntroY = drawIntroNote(doc, state, metaEndY);

  drawLinesTable(doc, state, mode, Math.max(47, afterIntroY));

  // lastAutoTable.finalY is a runtime field added by the plugin.
  const finalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 60;
  const afterTotalsY = drawTotals(doc, state, mode, finalY + 8);
  const afterClosingY = drawClosingNote(doc, state, afterTotalsY);
  const afterNotesY = drawNotesAndTerms(doc, state, afterClosingY);
  drawPaymentTerms(doc, paymentStages, afterNotesY);

  if (mode === "internal") {
    drawSupplierBreakdown(doc, state);
  }
  drawDesignsPages(doc, designsToEmbed);

  // Paint header + footer onto every page AFTER all content is laid out so
  // the page count is final and footer pagination labels are correct.
  paintAllPages(doc, profile, state, mode);

  const filename = `${state.reference || "quote"}_${mode}.pdf`;
  doc.save(filename);
  ctx.showToast(`${mode === "customer" ? "Customer" : "Internal"} PDF saved`, "success");
}

export function exportCustomerPdf(state: QuoteBuilderState, ctx: PageContext): void {
  void exportPdf(state, "customer", ctx).catch((err) => {
    console.error("[qb-pdf] customer export failed", err);
    ctx.showToast("Customer PDF export failed", "error");
  });
}

export function exportInternalPdf(state: QuoteBuilderState, ctx: PageContext): void {
  void exportPdf(state, "internal", ctx).catch((err) => {
    console.error("[qb-pdf] internal export failed", err);
    ctx.showToast("Internal PDF export failed", "error");
  });
}
